<script lang="ts">
	/**
	 * Main navigation bar, matching the live site.
	 *
	 * Three top-level items — Cannabis Seeds, Seed Sale, News & Blog — where
	 * "Cannabis Seeds" opens a two-column dropdown holding the nine category
	 * links. An earlier version listed all ten categories flat across the bar,
	 * which is not what the site does and reads as a wall of links.
	 *
	 * Column membership is taken from the live menu rather than derived, because
	 * it is an editorial grouping, not alphabetical or by product count.
	 */
	import { m } from '$lib/paraglide/messages';
	import { page } from '$app/state';
	import { getCategoriesContext } from '$lib/context';
	import { getLocale } from '$lib/paraglide/runtime';
	import { pagePath } from '$lib/pages';
	import { DERIVED_CATEGORIES } from '$lib/data/derived-categories';
	// From `services/category-path`, not `services/categories`: the latter reaches
	// the catalogue and every strain body with it, and this component is in the
	// header of every page. See the note in that module.
	import { categoryPath } from '$lib/services/category-path';
	import { prefixPath, type Locale } from '$lib/i18n';
	import { isActivePath, isCatalogPath } from '$lib/helpers/nav';

	const categoriesOf = getCategoriesContext();
	const categories = $derived(categoriesOf());

	/**
	 * Current-page highlighting.
	 *
	 * Safe on a fully prerendered site: `page.url.pathname` during prerendering is
	 * the path being written, so each page ships with its own item already marked
	 * and nothing has to be corrected on hydration.
	 */
	const currentPath = $derived(page.url.pathname);

	/**
	 * Categories and their order, matching the strains site (`dp-strains`
	 * `CategoryId`) so the two properties present the same taxonomy.
	 *
	 * Two differences from the raw WooCommerce list are deliberate:
	 * `cannabis-seeds` is omitted because it is the parent this dropdown hangs
	 * off, and `cbd-seeds` is included even though WooCommerce has no such
	 * category — it is derived from strain data, see `data/derived-categories`.
	 */
	const DROPDOWN_COLUMNS = [
		['indica-seeds', 'sativa-seeds', 'hybrid-seeds', 'feminised-seeds', 'autoflower-seeds'],
		['cbd-seeds', 'high-thc-seeds', 'indoor-seeds', 'outdoor-seeds', 'fast-flowering-cannabis-seeds']
	];

	/**
	 * Resolves English slugs to this locale's categories.
	 *
	 * Matching on `key` rather than `slug` is required: WPML translates category
	 * slugs, so comparing against `feminised-seeds` finds nothing in Thai.
	 * Categories absent from the catalogue are dropped rather than linked to a
	 * 404 — `seed-sale` is in the live menu but has no products.
	 */
	const columns = $derived(
		DROPDOWN_COLUMNS.map((column) =>
			column
				.map((key) => {
					const match = categories.find((c) => c.key === key);
					if (match) return match;

					// Derived categories have no WooCommerce entry but a real page.
					// Their labels live with the definition so there is one table,
					// not a second copy here that drifts out of step.
					const derived = DERIVED_CATEGORIES.find((c) => c.slug === key);
					if (!derived) return undefined;
					const locale = getLocale() as Locale;
					return {
						key,
						slug: key,
						label: derived.label[locale],
						fullSlug: categoryPath(locale, key)
					};
				})
				.filter((c) => c !== undefined)
		)
	);

	/**
	 * The whole catalogue. WooCommerce's `cannabis-seeds` category holds every
	 * product, so it is the localised "all" listing; `/shop` is the fallback for
	 * the case where that category is ever removed.
	 */
	const allSeeds = $derived(categories.find((c) => c.key === 'cannabis-seeds'));
	const allSeedsHref = $derived(allSeeds?.fullSlug ?? prefixPath('/shop', getLocale() as Locale));
	const seedSale = $derived(categories.find((c) => c.key === 'seed-sale'));
	// From layout data: only the server knows which locales have an archive.
	const blogHref = $derived((page.data.blogHref as string) ?? '/learn');

	/**
	 * Resellers has no Thai translation, so `pagePath` returns undefined there
	 * and the item is omitted rather than linking to a page that does not exist.
	 */
	const resellersHref = $derived(pagePath('resellers', getLocale()));

	/**
	 * Shared classes for a top-level item: an underline that wipes in from the
	 * left on hover and stays put on the current page, so hover and "you are here"
	 * are the same visual language rather than two different ones.
	 */
	const ITEM =
		'relative block px-6 py-3.5 text-body transition-colors hover:text-brand-500 ' +
		'after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:origin-left ' +
		'after:bg-brand-500 after:transition-transform after:duration-200 hover:after:scale-x-100 ' +
		'focus-visible:outline-brand-500';

	const itemClass = (active: boolean) =>
		`${ITEM} ${active ? 'text-brand-500 after:scale-x-100' : 'after:scale-x-0'}`;

	const blogActive = $derived(isActivePath(currentPath, blogHref));
</script>

<!--
	The dropdown is shown with CSS on hover/focus rather than an `{#if}`, so its
	category links are always present in the rendered HTML. Gating it on state
	meant the server-rendered page contained no category links at all — invisible
	to crawlers and to anyone without JavaScript.
-->
<nav class="border-t border-subtle" aria-label="Main">
	<!--
		Hairline rules between items rather than plain spacing, as on
		dutch-passion.com. `divide-x` puts them on the list, so the first item does
		not get a stray leading rule.
	-->
	<ul class="flex items-center divide-x divide-subtle text-sm font-bold tracking-wide uppercase">
		<li class="group relative">
			<a
				href={allSeedsHref}
				class="{itemClass(isCatalogPath(currentPath))} flex items-center gap-1 !pl-0"
				aria-current={isCatalogPath(currentPath) ? 'true' : undefined}
			>
				{m.cannabis_seeds()}
				<svg
					class="h-4 w-4 transition-transform duration-200 group-hover:rotate-180"
					viewBox="0 0 20 20"
					fill="currentColor"
					aria-hidden="true"
				>
					<path
						fill-rule="evenodd"
						d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
						clip-rule="evenodd"
					/>
				</svg>
			</a>

			<!--
				Slides down a few pixels as it fades, which reads as the panel coming
				out of the bar rather than materialising over it. `visibility` is in
				the transition list so it changes at the *end* of the fade out —
				without it the panel would vanish instantly and only the empty space
				would animate.
			-->
			<div
				class="invisible absolute top-full left-0 z-20 grid w-max -translate-y-1 grid-cols-2 gap-x-12 gap-y-2 rounded-b-md border border-subtle bg-surface p-6 opacity-0 shadow-lg transition-[opacity,transform,visibility] duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100"
			>
				{#each columns as column, i (i)}
					<ul class="grid content-start gap-2">
						{#each column as category (category.key)}
							{@const active = isActivePath(currentPath, category.fullSlug)}
							<li>
								<a
									href={category.fullSlug}
									aria-current={active ? 'page' : undefined}
									class="block rounded-sm px-2 py-1 text-sm font-normal whitespace-nowrap normal-case transition-colors hover:bg-surface-muted hover:text-brand-500 {active
										? 'bg-surface-muted font-semibold text-brand-600'
										: 'text-body'}"
								>
									{category.label}
								</a>
							</li>
						{/each}
					</ul>
				{/each}

				<!--
					The unfiltered listing sits below the categories, spanning both
					columns behind a divider, so it reads as the way out of the
					taxonomy rather than as an eleventh category.
				-->
				<a
					href={allSeedsHref}
					class="text-brand-500 hover:text-brand-600 col-span-2 mt-2 flex items-center justify-between gap-6 border-t border-subtle pt-3 text-sm font-semibold whitespace-nowrap normal-case"
				>
					{m.view_all_seeds()}
					<svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
						<path
							fill-rule="evenodd"
							d="M2 10a.75.75 0 01.75-.75h12.59l-2.1-1.95a.75.75 0 111.02-1.1l3.5 3.25a.75.75 0 010 1.1l-3.5 3.25a.75.75 0 11-1.02-1.1l2.1-1.95H2.75A.75.75 0 012 10z"
							clip-rule="evenodd"
						/>
					</svg>
				</a>
			</div>
		</li>

		{#if seedSale}
			{@const active = isActivePath(currentPath, seedSale.fullSlug)}
			<li>
				<!--
					Picked out in green, as the reference does with its own sale item. The
					`!` overrides the shared item colour rather than forking the class
					string for one entry.
				-->
				<a
					href={seedSale.fullSlug}
					class="{itemClass(active)} !text-secondary-600 hover:!text-secondary-700"
					aria-current={active ? 'page' : undefined}
				>
					{seedSale.label}
				</a>
			</li>
		{/if}

		<li>
			<a
				href={blogHref}
				class={itemClass(blogActive)}
				aria-current={blogActive ? 'page' : undefined}
			>
				{m.news_and_blog()}
			</a>
		</li>

		{#if resellersHref}
			{@const active = isActivePath(currentPath, resellersHref)}
			<li>
				<a href={resellersHref} class={itemClass(active)} aria-current={active ? 'page' : undefined}>
					{m.resellers()}
				</a>
			</li>
		{/if}
	</ul>
</nav>
